"use client";

import { Card, Collapse, MenuItem, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { useFormikContext } from "formik";
import { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { DATETIME_FILTER_FORMAT } from "@/helpers/date-helpers";
import useIntegrationFilterOptions from "@/hooks/data/use-integration-filter-options";
import { IServiceInterface } from "@/interfaces/service-interface.interface";
import { ServiceInterfaceMessages } from "@/messages/service-interface/service-interface.messages";
import { FilterType, IFilter } from "@/types/filter.types";

import { ServiceInterfaceFilterKey } from "../../types";

type Props = {
  messages: ServiceInterfaceMessages;
};

const GRID_SIZES = {
  LARGE: { lg: 3, md: 4, sm: 6, xs: 12 },
  BUTTON: { lg: 2, md: 4, sm: 6, xs: 12 },
} as const;

const DATE_TIME_PICKER_SLOT_PROPS = {
  textField: {
    fullWidth: true,
    InputLabelProps: { shrink: true },
  },
  layout: {
    sx: {
      "& .MuiDivider-root": { margin: 0 },
      backgroundImage: "unset",
    },
  },
  digitalClockSectionItem: {
    style: {
      minWidth: "unset",
    },
  },
} as const;

const ALL_VALUE = "";
const YES_VALUE = "true";
const NO_VALUE = "false";

const SITUATION_VALUES = {
  ALL: "",
  RUNNING: "running",
  PAUSED: "paused",
} as const;

type SituationValue = (typeof SITUATION_VALUES)[keyof typeof SITUATION_VALUES];

const SITUATION_FILTER_PROPERTIES = ["IsRunning", "IsPaused"] as const;

const EXECUTION_HISTORY_VALUES = {
  ALL: "",
  ERROR: "error",
  SUCCESS: "success",
} as const;

type ExecutionHistoryValue =
  (typeof EXECUTION_HISTORY_VALUES)[keyof typeof EXECUTION_HISTORY_VALUES];

const EXECUTION_HISTORY_FILTER_PROPERTIES = ["CountErrors", "CountSuccess"] as const;

function FiltersForm({ messages }: Props) {
  const { values, setValues, submitForm } = useFormikContext<IFilter<IServiceInterface>[]>();
  const { platformOptions } = useIntegrationFilterOptions();
  const [expanded, setExpanded] = useState(false);

  const getValueFromProperty = (property: string) =>
    values.find((f) => String(f.property) === property)?.value ?? "";

  const selectedPlatformName = String(getValueFromProperty("PlatformName"));

  const upsertFilter = (
    property: ServiceInterfaceFilterKey,
    value: string | number | boolean | null,
    filterType: FilterType
  ) => {
    setValues((prev) => {
      const without = prev.filter((f) => String(f.property) !== property);
      const isEmpty =
        value === null || value === "" || (typeof value === "number" && Number.isNaN(value));
      if (isEmpty) return without;

      return [
        ...without,
        {
          property: property as keyof IServiceInterface,
          filterType,
          value: value as string | number | boolean,
        },
      ];
    });
  };

  const handlePlatformChange = (platformName: string) => {
    if (!platformName) {
      setValues((prev) => prev.filter((f) => String(f.property) !== "PlatformName"));
      return;
    }
    upsertFilter("PlatformName", platformName, FilterType.Contains);
  };

  const handleBooleanFilter = (property: "IsActive", selected: string) => {
    if (selected === ALL_VALUE) {
      setValues((prev) => prev.filter((f) => String(f.property) !== property));
      return;
    }
    upsertFilter(property, selected === YES_VALUE, FilterType.Equals);
  };

  const getBooleanFilterValue = (property: string): string => {
    const filter = values.find((f) => String(f.property) === property);
    if (!filter) return ALL_VALUE;
    return filter.value === true || filter.value === "true" || filter.value === 1
      ? YES_VALUE
      : NO_VALUE;
  };

  const getSituationValue = (): SituationValue => {
    if (values.some((f) => String(f.property) === "IsRunning")) return SITUATION_VALUES.RUNNING;
    if (values.some((f) => String(f.property) === "IsPaused")) return SITUATION_VALUES.PAUSED;
    return SITUATION_VALUES.ALL;
  };

  const getSituationLabel = (value: SituationValue) => {
    switch (value) {
      case SITUATION_VALUES.RUNNING:
        return messages.runningOption;
      case SITUATION_VALUES.PAUSED:
        return messages.pausedOption;
      default:
        return messages.allOption;
    }
  };

  const handleSituationChange = (selected: SituationValue) => {
    setValues((prev) => {
      const without = prev.filter(
        (f) =>
          !SITUATION_FILTER_PROPERTIES.includes(
            String(f.property) as (typeof SITUATION_FILTER_PROPERTIES)[number]
          )
      );

      switch (selected) {
        case SITUATION_VALUES.RUNNING:
          return [
            ...without,
            { property: "IsRunning", filterType: FilterType.Equals, value: true },
          ] as typeof prev;
        case SITUATION_VALUES.PAUSED:
          return [
            ...without,
            { property: "IsPaused", filterType: FilterType.Equals, value: true },
          ] as typeof prev;
        default:
          return without;
      }
    });
  };

  const getExecutionHistoryValue = (): ExecutionHistoryValue => {
    if (
      values.some(
        (f) => String(f.property) === "CountErrors" && f.filterType === FilterType.GreaterThan
      )
    ) {
      return EXECUTION_HISTORY_VALUES.ERROR;
    }
    if (
      values.some(
        (f) => String(f.property) === "CountSuccess" && f.filterType === FilterType.GreaterThan
      )
    ) {
      return EXECUTION_HISTORY_VALUES.SUCCESS;
    }
    return EXECUTION_HISTORY_VALUES.ALL;
  };

  const getExecutionHistoryLabel = (value: ExecutionHistoryValue) => {
    switch (value) {
      case EXECUTION_HISTORY_VALUES.ERROR:
        return messages.errorOption;
      case EXECUTION_HISTORY_VALUES.SUCCESS:
        return messages.successOption;
      default:
        return messages.allOption;
    }
  };

  const handleExecutionHistoryChange = (selected: ExecutionHistoryValue) => {
    setValues((prev) => {
      const without = prev.filter(
        (f) =>
          !EXECUTION_HISTORY_FILTER_PROPERTIES.includes(
            String(f.property) as (typeof EXECUTION_HISTORY_FILTER_PROPERTIES)[number]
          )
      );

      switch (selected) {
        case EXECUTION_HISTORY_VALUES.ERROR:
          return [
            ...without,
            { property: "CountErrors", filterType: FilterType.GreaterThan, value: 0 },
          ] as typeof prev;
        case EXECUTION_HISTORY_VALUES.SUCCESS:
          return [
            ...without,
            { property: "CountSuccess", filterType: FilterType.GreaterThan, value: 0 },
          ] as typeof prev;
        default:
          return without;
      }
    });
  };

  const clearExpandedFilters = () => {
    setValues((prev) =>
      prev.filter(
        (f) =>
          String(f.property) !== "LastExecution" &&
          !EXECUTION_HISTORY_FILTER_PROPERTIES.includes(
            String(f.property) as (typeof EXECUTION_HISTORY_FILTER_PROPERTIES)[number]
          )
      )
    );
  };

  const handleLastExecutionFrom = (value: dayjs.Dayjs | null) => {
    setValues((prev) => {
      const without = prev.filter(
        (f) =>
          String(f.property) !== "LastExecution" || f.filterType !== FilterType.GreaterThan
      );
      if (!value) return without;
      return [
        ...without,
        {
          property: "LastExecution" as keyof IServiceInterface,
          value: value.toISOString(),
          filterType: FilterType.GreaterThan,
        },
      ];
    });
  };

  const handleLastExecutionTo = (value: dayjs.Dayjs | null) => {
    setValues((prev) => {
      const without = prev.filter(
        (f) =>
          String(f.property) !== "LastExecution" || f.filterType !== FilterType.LessThan
      );
      if (!value) return without;
      return [
        ...without,
        {
          property: "LastExecution" as keyof IServiceInterface,
          value: value.toISOString(),
          filterType: FilterType.LessThan,
        },
      ];
    });
  };

  const getLastExecutionFrom = () => {
    const filter = values.find(
      (f) => String(f.property) === "LastExecution" && f.filterType === FilterType.GreaterThan
    );
    return filter?.value ? dayjs(String(filter.value)) : null;
  };

  const getLastExecutionTo = () => {
    const filter = values.find(
      (f) => String(f.property) === "LastExecution" && f.filterType === FilterType.LessThan
    );
    return filter?.value ? dayjs(String(filter.value)) : null;
  };

  const handleToggleExpanded = () => {
    setExpanded((prev) => {
      if (prev) clearExpandedFilters();
      return !prev;
    });
  };

  const handleClearFilters = () => {
    setValues([]);
    setExpanded(false);
  };

  const renderPlatformValue = (selected: string) => {
    if (!selected) return "\u00a0";
    const platform = platformOptions.find((p) => p.name === selected);
    return platform?.displayName ?? selected;
  };

  const booleanSelect = (property: "IsActive", label: string) => (
    <TextField
      select
      label={label}
      value={getBooleanFilterValue(property)}
      onChange={(e) => handleBooleanFilter(property, e.target.value)}
      fullWidth
      slotProps={{
        inputLabel: { shrink: true },
        select: {
          displayEmpty: true,
          renderValue: (selected) => {
            if (!selected || selected === ALL_VALUE) return messages.allOption;
            if (selected === YES_VALUE) return messages.yesOption;
            if (selected === NO_VALUE) return messages.noOption;
            return messages.allOption;
          },
        },
      }}
    >
      <MenuItem value={ALL_VALUE}>{messages.allOption}</MenuItem>
      <MenuItem value={YES_VALUE}>{messages.yesOption}</MenuItem>
      <MenuItem value={NO_VALUE}>{messages.noOption}</MenuItem>
    </TextField>
  );

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          <Grid size={GRID_SIZES.LARGE}>
            <TextField
              select
              label={messages.platformNameLabel}
              value={selectedPlatformName}
              onChange={(e) => handlePlatformChange(e.target.value)}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
                select: {
                  displayEmpty: true,
                  renderValue: renderPlatformValue,
                },
              }}
            >
              {platformOptions.map((platform) => (
                <MenuItem key={platform.platformId} value={platform.name}>
                  {platform.displayName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={GRID_SIZES.LARGE}>
            <TextField
              label={messages.descriptionLabel}
              value={String(getValueFromProperty("Description"))}
              onChange={(e) =>
                upsertFilter("Description", e.target.value, FilterType.Contains)
              }
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={GRID_SIZES.LARGE}>{booleanSelect("IsActive", messages.isActiveLabel)}</Grid>
          <Grid size={GRID_SIZES.LARGE}>
            <TextField
              select
              label={messages.situationFilterLabel}
              value={getSituationValue()}
              onChange={(e) => handleSituationChange(e.target.value as SituationValue)}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
                select: {
                  displayEmpty: true,
                  renderValue: (selected) => getSituationLabel(selected as SituationValue),
                },
              }}
            >
              <MenuItem value={SITUATION_VALUES.ALL}>{messages.allOption}</MenuItem>
              <MenuItem value={SITUATION_VALUES.RUNNING}>{messages.runningOption}</MenuItem>
              <MenuItem value={SITUATION_VALUES.PAUSED}>{messages.pausedOption}</MenuItem>
            </TextField>
          </Grid>

          <Grid size={12}>
            <Collapse in={expanded} unmountOnExit>
              <Grid container spacing={2}>
                <Grid size={GRID_SIZES.LARGE}>
                  <TextField
                    select
                    label={messages.executionHistoryFilterLabel}
                    value={getExecutionHistoryValue()}
                    onChange={(e) =>
                      handleExecutionHistoryChange(e.target.value as ExecutionHistoryValue)
                    }
                    fullWidth
                    slotProps={{
                      inputLabel: { shrink: true },
                      select: {
                        displayEmpty: true,
                        renderValue: (selected) =>
                          getExecutionHistoryLabel(selected as ExecutionHistoryValue),
                      },
                    }}
                  >
                    <MenuItem value={EXECUTION_HISTORY_VALUES.ALL}>{messages.allOption}</MenuItem>
                    <MenuItem value={EXECUTION_HISTORY_VALUES.ERROR}>{messages.errorOption}</MenuItem>
                    <MenuItem value={EXECUTION_HISTORY_VALUES.SUCCESS}>
                      {messages.successOption}
                    </MenuItem>
                  </TextField>
                </Grid>
                <Grid size={GRID_SIZES.LARGE}>
                  <DateTimePicker
                    label={messages.lastExecutionFromLabel}
                    value={getLastExecutionFrom()}
                    onChange={handleLastExecutionFrom}
                    format={DATETIME_FILTER_FORMAT}
                    ampm={false}
                    timeSteps={{ minutes: 1 }}
                    slotProps={DATE_TIME_PICKER_SLOT_PROPS}
                  />
                </Grid>
                <Grid size={GRID_SIZES.LARGE}>
                  <DateTimePicker
                    label={messages.lastExecutionToLabel}
                    value={getLastExecutionTo()}
                    onChange={handleLastExecutionTo}
                    format={DATETIME_FILTER_FORMAT}
                    ampm={false}
                    timeSteps={{ minutes: 1 }}
                    slotProps={DATE_TIME_PICKER_SLOT_PROPS}
                  />
                </Grid>
              </Grid>
            </Collapse>

            <Grid container spacing={2} mt={expanded ? 2 : 0}>
              <Grid size={GRID_SIZES.BUTTON}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={handleToggleExpanded}
                  fullWidth
                >
                  {expanded ? messages.lessFiltersButton : messages.moreFiltersButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.BUTTON}>
                <MDButton variant="gradient" color="light" onClick={handleClearFilters} fullWidth>
                  {messages.clearFilterButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.BUTTON}>
                <MDButton variant="gradient" color="dark" onClick={submitForm} fullWidth>
                  {messages.filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default FiltersForm;
